/**
 * The project's memory as a picture: AI Workbench's Memory control (the Universe, the Map and the Timeline: one Canvas 2D
 * engine, one set of nodes seen three ways) on the project's hosted memory. The engine and its models are the
 * workbench's own files (provenance.json); this file is the console's stage around them, in place of the desktop's
 * MemoryGlobeStage and ArmsView: the data comes from the memory routes (useMemoryExplorerData.ts), and opening an item
 * hands it to the memory browser.
 */
import type { MemoryItem } from '@aioe/shared';
import { ArrowsExpand, Database, Magnifier, Minus, Plus, Sliders, Xmark } from '@gravity-ui/icons';
import { Button, Popover, Tooltip } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { timeAgo } from '../../hooks.ts';
import { ArtEmpty, ErrorNote, ListBlock, ListGroup, StatusMark, Toggle } from '../ui.tsx';
import { VIEW_TONES } from '../ui/Art.tsx';
import { type BrainHandle, MemoryBrainCanvas } from './MemoryBrainCanvas.tsx';
import { type GroupFocus, focusSet, toggleFocus } from './memoryFocusModel.ts';
import { type GlobeColours, resolveColours } from './memoryGlobeColours.ts';
import { MemoryGlobeLegend } from './MemoryGlobeLegend.tsx';
import { backStep, buildGlobe, defaultExpanded, expandedFor, foldHidden, type Globe, type GlobeLayer, isFoldable, LAYER_WORDS, MAX_NEIGHBOURS, searchGlobe } from './memoryGlobeModel.ts';
import { type MapLayoutKind, MAP_LAYOUTS } from './memoryMapLayouts.ts';
import { type ExplorerPicture, itemIdOfKey, KIND_WORD, neighboursOf } from './memoryExplorerModel.ts';
import { useMemoryExplorerData } from './useMemoryExplorerData.ts';
import './memoryExplorer.css';

type Picture = 'globe' | 'map' | 'timeline';
const PICTURES: ReadonlyArray<{ id: Picture; label: string }> = [
  { id: 'globe', label: 'Universe' },
  { id: 'map', label: 'Map' },
  { id: 'timeline', label: 'Timeline' },
];
/** The map's layouts the console offers: Shelves is a list of its own in the workbench, and the console's list is Browse. */
const LAYOUTS = MAP_LAYOUTS.filter((l) => l.id !== 'shelves');
/** Layers a project's memory never fills: the desktop's tools, routines and skills. Off, so the Timeline draws no empty lanes. */
const DESKTOP_ONLY: ReadonlySet<GlobeLayer> = new Set<GlobeLayer>(['apps', 'routines', 'skills']);
const NO_DEPTS: ReadonlySet<number> = new Set();
const PREFS_KEY = 'aioe.memoryExplorer';
/** The stage's own top bar: the picture centres below it. */
const HEADER_H = 60;
/** The card beside the picture, on a wide stage. */
const CARD_W = 300;
const NARROW = 640;
/** Before the first build answers. */
const NO_PICTURE: ExplorerPicture = { globe: buildGlobe([], { structure: true }), truncated: 0, adjacency: [], wires: [], wireWords: [], xlinks: [], xlinkWords: [], digest: '' };

interface Prefs {
  picture: Picture;
  layout: MapLayoutKind;
  motion: boolean;
}

function loadPrefs(): Prefs {
  const fallback: Prefs = { picture: 'globe', layout: 'rings', motion: true };
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<Prefs>;
    return {
      picture: PICTURES.some((p) => p.id === raw.picture) ? raw.picture! : fallback.picture,
      layout: LAYOUTS.some((l) => l.id === raw.layout) ? raw.layout! : fallback.layout,
      motion: typeof raw.motion === 'boolean' ? raw.motion : fallback.motion,
    };
  } catch {
    return fallback;
  }
}

/** The colours, read again when the console's theme changes. */
function useColours(globe: Globe): GlobeColours {
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    const mo = new MutationObserver(() => setEpoch((e) => e + 1));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => mo.disconnect();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => resolveColours(globe.departments, document.documentElement, globe.grouping), [globe.departments, globe.grouping, epoch]);
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

function useWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.round(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

export function MemoryExplorer({ projectId, projectName, rootNames, onOpenItem }: { projectId: string; projectName: string; rootNames: ReadonlyMap<string, string>; onOpenItem: (id: string) => void }) {
  const tone = VIEW_TONES.projects!;
  const data = useMemoryExplorerData(projectId, rootNames);
  const built = data.picture ?? NO_PICTURE;
  const { globe } = built;

  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const setPrefs = useCallback((patch: Partial<Prefs>) => {
    setPrefsState((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        // The choice lasts the visit.
      }
      return next;
    });
  }, []);
  const reducedMotion = useReducedMotion();
  const colours = useColours(globe);

  // Folders and groups opened in place; a new set of nodes keeps the ones it still has.
  const [fold, setFold] = useState<{ globe: Globe | null; open: ReadonlySet<string>; version: number }>({ globe: null, open: new Set(), version: 0 });
  if (fold.globe !== globe) setFold({ globe, open: fold.globe === null ? defaultExpanded(globe) : new Set([...fold.open].filter((k) => globe.byKey.has(k))), version: fold.version + 1 });
  const toggleFolder = useCallback((key: string) => setFold((f) => {
    const next = new Set(f.open);
    if (!next.delete(key)) next.add(key);
    return { ...f, open: next, version: f.version + 1 };
  }), []);
  const hidden = useMemo(() => foldHidden(globe, fold.open), [globe, fold.open]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [pointed, setPointed] = useState<{ key: string; relation: string } | null>(null);
  const [focus, setFocus] = useState<GroupFocus | null>(null);
  const focused = useMemo(() => focusSet(globe, focus), [globe, focus]);
  const [query, setQuery] = useState('');
  const hits = useMemo(() => searchGlobe(globe.nodes, query, 40), [globe, query]);
  const foundKeys = useMemo<ReadonlySet<string> | null>(() => (query.trim() ? new Set(hits.map((h) => h.node.key)) : null), [hits, query]);

  // A selection that went (the item was removed, or relabelled out of reach) lets go.
  const selected = selectedKey ? globe.byKey.get(selectedKey) : undefined;
  if (selectedKey && !selected && !data.filling && !data.loading) setSelectedKey(null);

  // What is selected or found sits in a closed folder: open the folders above it so it is on show.
  const revealKey = `${selectedKey ?? ''}|${foundKeys && foundKeys.size <= 80 ? [...foundKeys].join(',') : ''}|${fold.version}`;
  const [revealed, setRevealed] = useState('');
  if (revealed !== revealKey) {
    setRevealed(revealKey);
    const want: number[] = [];
    if (selected) want.push(selected.index);
    if (foundKeys && foundKeys.size <= 80) for (const k of foundKeys) {
      const n = globe.byKey.get(k);
      if (n) want.push(n.index);
    }
    const next = expandedFor(globe, fold.open, want);
    if (next !== fold.open) setFold((f) => ({ ...f, open: next, version: f.version + 1 }));
  }

  const select = useCallback(
    (key: string | null) => {
      setSelectedKey(key);
      setPointed(null);
      if (!key) return;
      // Pressing a group of a big layer opens it; Back or Escape closes it again.
      const hub = globe.byKey.get(key);
      if (hub && hub.struct === 'group' && isFoldable(globe, hub) && hub.children.length > 0 && !fold.open.has(key)) toggleFolder(key);
    },
    [globe, fold.open, toggleFolder],
  );
  const open = useCallback(
    (key: string) => {
      if (key.startsWith('dir:')) return toggleFolder(key);
      const id = itemIdOfKey(key);
      if (id) onOpenItem(id);
    },
    [toggleFolder, onOpenItem],
  );
  const goBack = useCallback((): boolean => {
    const step = backStep(globe, fold.open, selectedKey);
    if (!step.close) return false;
    toggleFolder(step.close);
    setSelectedKey(step.select ?? null);
    return true;
  }, [globe, fold.open, selectedKey, toggleFolder]);

  const stageRef = useRef<HTMLDivElement>(null);
  const width = useWidth(stageRef);
  const narrow = width > 0 && width < NARROW;
  const insetRight = selected && !narrow ? CARD_W + 16 : 0;
  const handle = useRef<BrainHandle | null>(null);
  const centreRef = useRef<HTMLDivElement>(null);
  const [now] = useState(() => Date.now());
  const total = globe.nodes.filter((n) => !n.struct).length;

  return (
    <ListGroup
      title="Explore"
      count={data.loading ? undefined : total}
      description="Everything in this memory you may read, drawn as one picture: turn it, zoom in, press anything to see what it connects to."
      actions={
        <Segment aria-label="Picture" size="sm" selectedKey={prefs.picture} onSelectionChange={(k) => setPrefs({ picture: String(k) as Picture })} className="w-fit">
          {PICTURES.map((p) => (
            <Segment.Item key={p.id} id={p.id}>
              {p.label}
            </Segment.Item>
          ))}
        </Segment>
      }
    >
      <ListBlock flush>
      <div
        ref={stageRef}
        className="mx-stage"
        data-memory-explorer={prefs.picture}
        data-memory-explorer-nodes={total}
        data-memory-explorer-first-ms={data.firstMs}
        data-memory-explorer-all-ms={data.allMs}
      >
        {data.loading ? (
          <div className="mx-stage__note">
            <StatusMark state="working" label="Reading the memory…" />
          </div>
        ) : total === 0 ? (
          <div className="mx-stage__note">
            <ArtEmpty tone={tone} icon={<Database />} title={data.itemsFiltered > 0 ? 'Nothing here you can read' : 'Nothing to draw yet'} compact>
              {data.itemsFiltered > 0
                ? `${data.itemsFiltered.toLocaleString()} ${data.itemsFiltered === 1 ? 'item is' : 'items are'} kept from you by their labels.`
                : 'A member’s workbench sends what it reads from each repository, and agents write notes, lessons and decisions back. They are drawn here.'}
            </ArtEmpty>
          </div>
        ) : (
          <>
            <MemoryBrainCanvas
              globe={globe}
              colours={colours}
              picture={prefs.picture}
              layoutKind={prefs.layout}
              links={built.wires}
              xlinks={built.xlinks}
              fold={hidden}
              expanded={fold.open}
              foldVersion={fold.version}
              focus={focused}
              litLink={pointed}
              adjacency={built.adjacency}
              selectedKey={selected ? selectedKey : null}
              pointedKey={pointed?.key ?? null}
              foundKeys={foundKeys}
              offLayers={DESKTOP_ONLY}
              offDepts={NO_DEPTS}
              insetLeft={0}
              insetRight={insetRight}
              motion={prefs.motion}
              speed={0.5}
              reducedMotion={reducedMotion}
              centreRef={centreRef}
              centreFile={projectName}
              now={now}
              handleRef={handle}
              onSelect={select}
              onOpen={open}
              onLayerPress={(layer) => setFocus((cur) => toggleFocus(cur, { layer }))}
              onBack={goBack}
              headerH={HEADER_H}
            />
            <div ref={centreRef} className="mx-centre" aria-hidden="true" style={{ visibility: prefs.picture === 'timeline' ? 'hidden' : 'visible' }}>
              <div className="mx-centre__inner">
                <span className="mx-centre__mark" />
                <span className="mx-centre__name">{projectName}</span>
              </div>
            </div>

            <div className="mx-stage__top" style={{ right: insetRight + 12 }}>
              <Find query={query} onQuery={setQuery} hits={hits.slice(0, 8)} onPick={(key) => (select(key), setQuery(''))} />
              <ViewOptions prefs={prefs} onChange={setPrefs} reducedMotion={reducedMotion} />
            </div>

            {!(narrow && selected) && <MemoryGlobeLegend globe={globe} colours={colours} grouping={globe.grouping} offLayers={DESKTOP_ONLY} focus={focus} onFocus={(next) => setFocus((cur) => toggleFocus(cur, next))} insetLeft={0} insetRight={insetRight + 120} chip={narrow} />}

            <div className="mx-stage__zoom" style={{ right: insetRight + 12 }}>
              <IconButton label="Zoom out" onPress={() => handle.current?.zoomBy(0.8)}>
                <Minus />
              </IconButton>
              <IconButton label="Zoom in" onPress={() => handle.current?.zoomBy(1.25)}>
                <Plus />
              </IconButton>
              <IconButton label="Show all of it" onPress={() => handle.current?.fit()}>
                <ArrowsExpand />
              </IconButton>
            </div>

            {selected && (
              <Card
                globe={globe}
                index={selected.index}
                neighbours={neighboursOf(built, selected.index)}
                item={itemIdOfKey(selected.key) ? data.itemOf(itemIdOfKey(selected.key)!) : undefined}
                expanded={fold.open}
                narrow={narrow}
                onSelect={select}
                onPoint={setPointed}
                onOpen={open}
                onFold={() => toggleFolder(selected.key)}
                onClose={() => select(null)}
              />
            )}
          </>
        )}
      </div>
      </ListBlock>
      <Foot data={data} total={total} truncated={built.truncated} />
    </ListGroup>
  );
}

function IconButton({ label, onPress, children }: { label: string; onPress: () => void; children: React.ReactNode }) {
  return (
    <Tooltip delay={300}>
      <Button size="sm" variant="ghost" isIconOnly aria-label={label} className="arms-panel size-8 min-w-8 rounded-full text-foreground" onPress={onPress}>
        {children}
      </Button>
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
}

/** Find: a name, a topic or a file; the best answers under the field, every answer lit on the picture. */
function Find({ query, onQuery, hits, onPick }: { query: string; onQuery: (q: string) => void; hits: ReturnType<typeof searchGlobe>; onPick: (key: string) => void }) {
  return (
    <div className="relative min-w-0 max-w-72 flex-1">
      <div className="arms-panel flex h-8 items-center gap-1.5 rounded-full px-3">
        <Magnifier className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hits[0]) onPick(hits[0].node.key);
            else if (e.key === 'Escape') onQuery('');
          }}
          placeholder="Find a concept, topic or file"
          aria-label="Find in the memory"
        />
        {query && (
          <button type="button" className="text-muted hover:text-foreground" aria-label="Clear" onClick={() => onQuery('')}>
            <Xmark className="size-3.5" />
          </button>
        )}
      </div>
      {query.trim() && (
        <ul className="arms-panel absolute inset-x-0 top-10 z-10 grid max-h-72 gap-0.5 overflow-y-auto rounded-2xl p-1.5" data-memory-scroll role="listbox" aria-label="Found">
          {hits.length === 0 && <li className="px-2 py-1.5 text-xs text-muted">Nothing you can read answers that.</li>}
          {hits.map((h) => (
            <li key={h.node.key}>
              <button type="button" className="grid w-full gap-0.5 rounded-lg px-2 py-1.5 text-left hover:bg-surface-secondary" onClick={() => onPick(h.node.key)}>
                <span className="truncate text-sm text-foreground">{h.node.label}</span>
                <span className="truncate text-xs text-muted">
                  {LAYER_WORDS[h.node.layer]}
                  {` · ${h.node.layer === 'memory' ? (h.node.topic ?? h.node.dept) : h.node.dept}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The picture's options in one place: how the map is laid out, and whether it moves. */
function ViewOptions({ prefs, onChange, reducedMotion }: { prefs: Prefs; onChange: (p: Partial<Prefs>) => void; reducedMotion: boolean }) {
  return (
    <Popover>
      <Tooltip delay={300}>
        <Button size="sm" variant="ghost" isIconOnly aria-label="Picture options" className="arms-panel size-8 min-w-8 rounded-full text-foreground">
          <Sliders />
        </Button>
        <Tooltip.Content>Picture options</Tooltip.Content>
      </Tooltip>
      <Popover.Content placement="bottom end" className="w-64 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog aria-label="Picture options" className="grid gap-3 p-3">
          <div className="grid gap-1.5">
            <span className="text-xs text-muted">Map layout</span>
            <Segment aria-label="Map layout" size="sm" selectedKey={prefs.layout} onSelectionChange={(k) => onChange({ layout: String(k) as MapLayoutKind, picture: 'map' })} className="w-fit">
              {LAYOUTS.map((l) => (
                <Segment.Item key={l.id} id={l.id}>
                  {l.label}
                </Segment.Item>
              ))}
            </Segment>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className={reducedMotion ? 'text-muted' : ''}>{reducedMotion ? 'Still: your system asks for less motion' : 'Turn slowly'}</span>
            <Toggle checked={prefs.motion && !reducedMotion} isDisabled={reducedMotion} onChange={(v) => onChange({ motion: v })} label="Turn slowly" />
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** What the selection is, what it connects to, and the one thing to do with it. */
function Card(p: {
  globe: Globe;
  index: number;
  neighbours: ReadonlyArray<{ index: number; kind: string; relation: string; count: number }>;
  item?: MemoryItem;
  expanded: ReadonlySet<string>;
  narrow: boolean;
  onSelect: (key: string) => void;
  onPoint: (lit: { key: string; relation: string } | null) => void;
  onOpen: (key: string) => void;
  onFold: () => void;
  onClose: () => void;
}) {
  const n = p.globe.nodes[p.index]!;
  const foldable = isFoldable(p.globe, n);
  const under = n.struct ? n.weight : 0;
  const kind = p.item ? KIND_WORD[p.item.kind] : n.struct === 'dir' ? 'Folder' : n.struct === 'group' ? (n.layer === 'memory' ? 'Topic' : 'Group') : LAYER_WORDS[n.layer];
  const where = n.layer === 'memory' && !n.struct ? (n.topic ?? n.dept) : n.struct ? LAYER_WORDS[n.layer] : n.dept;
  const shown = p.neighbours.filter((x) => x.kind === 'wire' || x.kind === 'xlink' || !p.globe.nodes[x.index]!.struct).slice(0, MAX_NEIGHBOURS);
  return (
    <section className={`arms-panel mx-card ${p.narrow ? 'mx-card--sheet' : ''}`} style={p.narrow ? undefined : { width: CARD_W }} aria-label={`About ${n.label}`} data-memory-scroll>
      <div className="flex items-start justify-between gap-2">
        <div className="grid min-w-0 gap-0.5">
          <span className="text-xs text-muted">
            {kind}
            {where ? ` · ${where}` : ''}
          </span>
          <h3 className="m-0 text-card font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">{n.label}</h3>
        </div>
        <Button size="sm" variant="ghost" isIconOnly aria-label="Close" className="-mr-1 -mt-1 shrink-0" onPress={p.onClose}>
          <Xmark />
        </Button>
      </div>
      {n.detail && <p className="m-0 font-mono text-xs text-muted [overflow-wrap:anywhere]">{n.detail}</p>}
      {p.item && <p className="m-0 text-xs text-muted">Changed {timeAgo(p.item.updatedAt)}</p>}
      {n.struct && <p className="m-0 text-sm text-muted tabular-nums">{under.toLocaleString()} {under === 1 ? 'item' : 'items'} under it</p>}
      {shown.length > 0 && (
        <div className="grid gap-1">
          <span className="text-xs text-muted">Connected to</span>
          <ul className="m-0 grid list-none gap-0.5 p-0">
            {shown.map((x) => {
              const other = p.globe.nodes[x.index]!;
              return (
                <li key={x.index}>
                  <button
                    type="button"
                    className="flex w-full items-baseline gap-2 rounded-lg px-1.5 py-1 text-left hover:bg-surface-secondary"
                    onClick={() => p.onSelect(other.key)}
                    onPointerEnter={() => p.onPoint({ key: other.key, relation: x.relation })}
                    onPointerLeave={() => p.onPoint(null)}
                  >
                    <span className="shrink-0 text-xs text-muted">{x.relation}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{other.label}</span>
                    {x.count > 1 && <span className="shrink-0 text-xs tabular-nums text-muted">×{x.count}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {p.item ? (
          <Button size="sm" onPress={() => p.onOpen(n.key)}>
            Open
          </Button>
        ) : foldable ? (
          <Button size="sm" variant="secondary" onPress={p.onFold}>
            {p.expanded.has(n.key) ? 'Fold it away' : 'Show what is in it'}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** Under the picture: how much of the memory is drawn, while the rest is still arriving. */
function Foot({ data, total, truncated }: { data: ReturnType<typeof useMemoryExplorerData>; total: number; truncated: number }) {
  if (data.loading) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      {data.filling ? (
        <StatusMark state="working" label={`Drawing ${total.toLocaleString()} so far${data.known ? ` of ${data.known.toLocaleString()} found` : ''}; the rest is on its way`} />
      ) : (
        <span className="text-muted">
          <span className="tabular-nums">{total.toLocaleString()}</span> {total === 1 ? 'item' : 'items'} drawn
          {truncated > 0 ? `; ${truncated.toLocaleString()} more concepts are left off to keep the picture smooth (Browse lists them)` : ''}
          {data.itemsFiltered > 0 ? `; ${data.itemsFiltered.toLocaleString()} kept from you by their labels` : ''}. It keeps itself up to date.
        </span>
      )}
      <ErrorNote error={data.error} />
    </div>
  );
}
